import { MarkGithubIcon } from "@primer/octicons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, token } from "../api/client";
import { useProviders } from "../api/hooks";
import type { Actor, AuthProvider } from "../api/types";
import { ErrorMessage } from "../components/common";
import { BeeMascot } from "../components/BeeMascot";
import { Alert, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Field } from "../components/ui/field";
import { Input } from "../components/ui/input";

/** Where to go after signing in: a path on this site only (the server checks again). */
function nextPath(p: URLSearchParams): string {
  const n = p.get("next") ?? "/";
  return n.startsWith("/") && !n.startsWith("//") ? n : "/";
}

export function Login() {
  const [params] = useSearchParams();
  const providers = useProviders();
  const list = providers.data ?? [];
  const next = nextPath(params);
  const failed = params.get("error");
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-8">
      <div className="mb-4">
        <BeeMascot state={failed ? "failed" : "idle"} cell={4} />
      </div>
      <h1 className="mb-4 text-2xl font-light tracking-[-.5px]">Sign in to bee</h1>
      {failed && (
        <Alert variant="error" className="mb-4 mt-0 w-[min(340px,100%)]">
          <AlertTitle>Sign-in failed</AlertTitle>
          {failed}
        </Alert>
      )}
      {list.length > 0 && (
        <div className="flex w-[min(340px,100%)] flex-col gap-2 rounded-md border border-border-muted bg-muted p-4">
          {list.map((p) => (
            <ProviderButton key={p.id} provider={p} next={next} />
          ))}
          <p className="mt-1 text-center text-xs text-muted-foreground">
            bee reads only your profile. You grant access to repositories later, when you import or sync one.
          </p>
        </div>
      )}
      <p className="mt-4 w-[min(340px,100%)] text-center text-xs text-muted-foreground">
        bee is source control for teams and AI agents. This site never asks for your GitHub password: those sign-ins happen on
        the provider's own site.
      </p>
      {list.length > 0 ? (
        <details className="mt-4 w-[min(340px,100%)] text-center">
          <summary className="cursor-pointer text-muted-foreground">Sign in with an access token</summary>
          <TokenForm next={next} />
        </details>
      ) : (
        <TokenForm next={next} />
      )}
    </div>
  );
}

/** Starts a sign-in, which asks the provider only for the profile; with connect, also for repository access. */
function loginHref(provider: string, next: string, connect?: boolean) {
  const q = new URLSearchParams({ next });
  if (connect) q.set("connect", "1");
  return `/auth/${provider}/login?${q}`;
}

/** Grants bee access to the user's repositories on provider, back to next afterwards. */
export const connectHref = (provider: string, next: string) => loginHref(provider, next, true);

export function ProviderButton({ provider, next, connect, label }: { provider: AuthProvider; next: string; connect?: boolean; label?: string }) {
  const Icon = MarkGithubIcon;
  return (
    <Button asChild className="w-full justify-center">
      {/* A full navigation: the server redirects to the provider. */}
      <a href={loginHref(provider.id, next, connect)}>
        <Icon /> {label ?? `Continue with ${provider.name}`}
        {provider.host !== "github.com" && <span className="text-muted-foreground">({provider.host})</span>}
      </a>
    </Button>
  );
}

function TokenForm({ next }: { next: string }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const nav = useNavigate();
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    token.set(value.trim());
    try {
      const r = await api.get<{ actor: Actor | null }>("/v1/whoami");
      if (!r.actor) throw new Error("token not accepted");
      qc.clear();
      nav(next);
    } catch (err) {
      token.set(null);
      setError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-4 w-[min(340px,100%)] text-left">
      {error ? (
        <div className="mb-4 [&>[data-slot=alert]]:mt-0">
          <ErrorMessage error={error} />
        </div>
      ) : null}
      <form className="rounded-md border border-border-muted bg-muted p-4" onSubmit={submit}>
        <Field label="Access token">
          <Input mono type="password" value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
        <Button variant="primary" className="mt-4 w-full" type="submit" disabled={!value.trim() || busy}>
          {busy ? "Checking…" : "Sign in"}
        </Button>
      </form>
      <div className="mt-4 rounded-md border p-4 text-center text-muted-foreground">
        Need a token? An admin issues one with <code>bee admin token &lt;name&gt;</code>. It is kept in this browser only.
      </div>
    </div>
  );
}
